import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import {
  LOGISTIC_PROVIDERS_LIST_FILTERS_BASE,
  LOGISTIC_PROVIDER_TABLE_COLUMNS,
  LogisticProviderFilters,
  getRenderLogisticProviderTableCell,
} from '@lp/features/admin/tables/LogisticProvidersListTable';
import { AuthContext } from '@lp/core/auth';
import { usePartnerProvidersListQuery, useRemovePartnerProviderMutation } from '@/queries/partner-providers';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';

export const LogisticProvidersPartnersList = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: LOGISTIC_PROVIDERS_LIST_FILTERS_BASE,
    query: usePartnerProvidersListQuery,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemovePartnerProviderMutation,
    successMessage: 'Logistics Provider deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/logistics_providers/supplemental_lift/create');
  };

  const handleEdit = useCallback(
    (supplementalLiftProviderId: string) => {
      navigate(`/admin/logistics_providers/supplemental_lift/${supplementalLiftProviderId}`);
    },
    [navigate],
  );

  const renderLogisticProviderTableCell = useMemo(() => {
    return getRenderLogisticProviderTableCell(handleEdit, onModalOpen, true, hasCreatePermissions);
  }, [handleEdit, onModalOpen, hasCreatePermissions]);

  const LOGISTIC_PROVIDER_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions || hasEditPermissions
      ? LOGISTIC_PROVIDER_TABLE_COLUMNS
      : LOGISTIC_PROVIDER_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions, hasEditPermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="Supplemental Lift Providers">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <LogisticProviderFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="Supplemental Lift Providers"
            columns={LOGISTIC_PROVIDER_TABLE_COLUMNS_FILTERED}
            renderCell={renderLogisticProviderTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Supplemental Lift Provider?"
        description="Are you sure you want to delete the Supplemental Lift Provider?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
